import React, { useState, useEffect } from 'react';
import { 
  BarChart3, PieChart, TrendingUp, Calendar, Loader2,
  ArrowUp, ArrowDown, DollarSign, Building, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { gastosAPI } from '../api';
import { toast } from '../hooks/use-toast';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2 
  }).format(amount || 0);
};

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const COLORES = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', 
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
];

const EstadisticasGastos = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_controles: 0,
    total_importes: 0,
    total_iva: 0,
    total_irpf: 0,
    total_coste: 0,
    gastos_por_tipo: [],
    gastos_por_mes: [],
    top_proveedores: []
  });
  
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const params = {};
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      
      const response = await gastosAPI.getStats(params);
      setStats(response.data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las estadísticas',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFiltrar = () => {
    loadStats();
  };

  // Calcular el máximo para los gráficos de barras
  const maxGastoTipo = Math.max(...(stats.gastos_por_tipo || []).map(g => g.total), 1);
  const maxGastoMes = Math.max(...(stats.gastos_por_mes || []).map(g => g.total), 1);
  const maxProveedor = Math.max(...(stats.top_proveedores || []).map(p => p.total), 1);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Estadísticas de Gastos
          </h1>
          <p className="text-gray-600 mt-1">Análisis visual de los gastos del despacho</p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-end gap-4">
            <div>
              <Label>Fecha desde</Label>
              <Input 
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
              />
            </div>
            <div>
              <Label>Fecha hasta</Label>
              <Input 
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
              />
            </div>
            <Button onClick={handleFiltrar} className="bg-blue-600 hover:bg-blue-700">
              <Filter className="w-4 h-4 mr-2" />
              Filtrar
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setFechaDesde('');
                setFechaHasta('');
                loadStats();
              }}
            >
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resumen General */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Controles</p>
                <p className="text-3xl font-bold">{stats.total_controles}</p>
              </div>
              <div className="w-12 h-12 bg-blue-400/30 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <p className="text-gray-600 text-sm">Base Imponible</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_importes)}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <p className="text-gray-600 text-sm">IVA Soportado</p>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(stats.total_iva)}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <p className="text-gray-600 text-sm">IRPF Retenido</p>
            <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.total_irpf)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Total Gastos</p>
                <p className="text-3xl font-bold">{formatCurrency(stats.total_coste)}</p>
              </div>
              <div className="w-12 h-12 bg-green-400/30 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gastos por Tipo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <PieChart className="w-5 h-5 text-blue-600" />
              Gastos por Tipo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.gastos_por_tipo && stats.gastos_por_tipo.length > 0 ? (
              <div className="space-y-3">
                {stats.gastos_por_tipo.map((item, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-700 truncate max-w-[200px]" title={item.tipo}>
                        {item.tipo}
                      </span>
                      <span className="text-sm font-medium">{formatCurrency(item.total)}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="h-3 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${(item.total / maxGastoTipo) * 100}%`,
                          backgroundColor: COLORES[index % COLORES.length]
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{item.cantidad} registros</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No hay datos disponibles</p>
            )}
          </CardContent>
        </Card>

        {/* Top Proveedores */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building className="w-5 h-5 text-green-600" />
              Top 10 Proveedores
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.top_proveedores && stats.top_proveedores.length > 0 ? (
              <div className="space-y-3">
                {stats.top_proveedores.map((item, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                          {index + 1}
                        </span>
                        <span className="text-sm text-gray-700 truncate max-w-[180px]" title={item.proveedor}>
                          {item.proveedor}
                        </span>
                      </div>
                      <span className="text-sm font-medium">{formatCurrency(item.total)}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 ml-8">
                      <div 
                        className="h-2 rounded-full bg-green-500 transition-all duration-500"
                        style={{ width: `${(item.total / maxProveedor) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No hay datos disponibles</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Gastos por Mes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5 text-purple-600" />
            Evolución Mensual de Gastos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.gastos_por_mes && stats.gastos_por_mes.length > 0 ? (
            <div className="h-64">
              <div className="flex items-end justify-between h-full gap-2">
                {stats.gastos_por_mes.map((item, index) => {
                  const altura = (item.total / maxGastoMes) * 100;
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-gradient-to-t from-purple-500 to-purple-400 rounded-t transition-all duration-500 relative group"
                        style={{ height: `${Math.max(altura, 5)}%` }}
                      >
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          {formatCurrency(item.total)}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {MESES[item.month - 1]} {item.year.toString().slice(-2)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No hay datos disponibles</p>
          )}
        </CardContent>
      </Card>

      {/* Resumen Detallado */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Resumen Detallado por Tipo</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.gastos_por_tipo && stats.gastos_por_tipo.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Tipo de Gasto</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">Registros</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">% del Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {stats.gastos_por_tipo.map((item, index) => {
                    const porcentaje = stats.total_coste > 0 
                      ? ((item.total / stats.total_coste) * 100).toFixed(1) 
                      : 0;
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: COLORES[index % COLORES.length] }}
                            />
                            {item.tipo}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-right">{item.cantidad}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(item.total)}</td>
                        <td className="px-4 py-3 text-sm text-right">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {porcentaje}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-100 font-bold">
                  <tr>
                    <td className="px-4 py-3 text-sm">TOTAL</td>
                    <td className="px-4 py-3 text-sm text-right">{stats.total_controles}</td>
                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(stats.total_coste)}</td>
                    <td className="px-4 py-3 text-sm text-right">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No hay datos disponibles</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EstadisticasGastos;
